import type { AddressInfo } from '../../../schemas/dancer-form.schema';

// Re-export AddressSchema + AddressInfo from the canonical schema so
// consumers can import schema + component from one entry point.
// (Stretch criterion 2 in WIP_consolidating_address_input_component_2026_05_22.)
export {
  AddressSchema,
  type AddressInfo,
} from '../../../schemas/dancer-form.schema';

/**
 * Canonical address value type. Alias of {@link AddressInfo} from the
 * dancer-form schema. Use this type at the seams where the component
 * exchanges values with consumers (RHF field paths, mappers, tests).
 */
export type AddressValue = AddressInfo;

/**
 * Per-field error map for inline display under each input.
 *
 * RHF surfaces these as `formState.errors.<name>.<field>?.message`.
 * The self-wired component derives this map from `useFormContext()` —
 * consumers do not pass it explicitly.
 */
export interface AddressFieldError {
  streetAddress?: string;
  apartment?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  countryCode?: string;
}

/**
 * Public props for the self-wired `AddressInput`.
 *
 * Required: `name` (RHF field path). The component calls
 * `useFormContext()` and `useController({ name })` internally; the
 * parent never passes value/onChange/errors.
 *
 * `mode="view"` renders a read-only address block instead of inputs.
 * Useful for hubs that toggle between display and edit on the same
 * form slot without duplicating presentation JSX.
 */
export interface AddressInputProps {
  /** RHF field path. The slot at this path must conform to {@link AddressValue}. */
  name: string;
  /** Editor mode. Defaults to `'edit'`. */
  mode?: 'edit' | 'view';
  /** Surface the `*` marker on required fields. Has no effect in `mode="view"`. */
  required?: boolean;
  className?: string;
  label?: string;
  hint?: string;
  /** Optional override for the Places API key. Defaults to `NEXT_PUBLIC_GOOGLE_PLACES_API_KEY`. */
  apiKey?: string;
  /**
   * Notified when the Google Places integration fails (load error, two
   * consecutive callback timeouts, or missing API key). Lets the host
   * surface a wizard-level cue and forward the error to Sentry.
   */
  onAutocompleteError?: (
    error: Error,
    op: 'getDetails' | 'getPredictions' | 'load',
  ) => void;
}

/**
 * Default values for a freshly-initialised address slot. Use to seed
 * the parent form's `defaultValues` if it doesn't already provide an
 * address sub-object.
 */
export const DEFAULT_ADDRESS_VALUE: AddressValue = {
  streetAddress: '',
  apartment: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  countryCode: 'CH',
};
